import React, { useCallback, useMemo, useState } from 'react';
import { hot } from 'react-hot-loader/root';
import {
  CssBaseline,
  createMuiTheme,
  ThemeProvider,
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@material-ui/core';
import { Router, useHistory } from 'react-router-dom';
import history from '@/history';
import Routes from '@/routes';
import { useAuth0 } from '@cmr/react-auth0';
import { Loading, NoAccess, UpdateNotify, AppBar } from '@cmr/react-ui-common';
import { ClientsProvider } from '@cmr/react-clients-api';
import { yellow } from '@material-ui/core/colors';
import { SessionsProvider } from '../features/sessions/context';
import { QuestionnairesProvider } from '../features/questionnaires/context';
import { DatasetsProvider } from '../features/datasets/context';
import { SnackbarProvider } from 'notistack';
import { SignalRProvider } from '@/features/signalr/context';
import cuid from 'cuid';
import { permissions, routePaths } from '@/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import DateFnsUtils from '@date-io/date-fns';

var sessionId = sessionStorage.sessionId
  ? sessionStorage.sessionId
  : (sessionStorage.sessionId = cuid());

const AppBarWrapper: React.FC = () => {
  const history = useHistory();
  const { hasPermission } = useAuth0();
  const isDatasetAdmin = hasPermission(permissions.datasetAdmin);
  const isQuestionnaireAdmin = hasPermission(permissions.questionnaireAdmin);
  const isAdmin = isDatasetAdmin || isQuestionnaireAdmin;
  const { push } = useHistory();

  const onSearch = useCallback(
    (value: string) => {
      if (value) history.push(`/survey/notice/${value.trim()}`);
    },
    [history],
  );
  const [adminMenuAnchor, setAdminMenuAnchor] = useState<HTMLElement | null>(
    null,
  );

  const handleAdminMenu = (e: React.MouseEvent<HTMLElement>) => {
    setAdminMenuAnchor(e.currentTarget);
  };

  return (
    <AppBar
      appName='Inflow'
      color={yellow['900']}
      showSearchBox
      position='fixed'
      searchBoxProps={{
        clearOnSearch: true,
        onSearch,
        placeholder: 'Loss Notice #',
      }}
    >
      {isAdmin && (
        <IconButton
          color='inherit'
          onClick={handleAdminMenu}
          aria-label='Admin Menu'
          aria-controls='menu-admin'
          aria-haspopup='true'
        >
          <FontAwesomeIcon icon={['fad', 'tools']} />
        </IconButton>
      )}
      <Menu
        id='menu-admin'
        anchorEl={adminMenuAnchor}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={Boolean(adminMenuAnchor)}
        onClose={() => setAdminMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            push(routePaths.admin.varassetExports);
            setAdminMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <FontAwesomeIcon icon={['fad', 'file-export']} />
          </ListItemIcon>
          <ListItemText primary='Varasset Exports' />
        </MenuItem>
        <MenuItem
          onClick={() => {
            push(routePaths.reporting.mediaAttachment);
            setAdminMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <FontAwesomeIcon icon={['fad', 'photo-video']} />
          </ListItemIcon>
          <ListItemText primary='SCF Media Report' />
        </MenuItem>
        <MenuItem
          onClick={() => {
            push(routePaths.admin.clientId.index);
            setAdminMenuAnchor(null);
          }}
        >
          <ListItemIcon>
            <FontAwesomeIcon icon={['fad', 'hashtag']} />
          </ListItemIcon>
          <ListItemText primary='Work Order Numbers' />
        </MenuItem>
        {isDatasetAdmin && (
          <MenuItem
            onClick={() => {
              push(routePaths.admin.datasets.index);
              setAdminMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={['fad', 'table']} />
            </ListItemIcon>
            <ListItemText primary='Datasets' />
          </MenuItem>
        )}
        {isQuestionnaireAdmin && (
          <MenuItem
            onClick={() => {
              push(routePaths.admin.questionnaires.index);
              setAdminMenuAnchor(null);
            }}
          >
            <ListItemIcon>
              <FontAwesomeIcon icon={['fad', 'question-circle']} />
            </ListItemIcon>
            <ListItemText primary='Questionnaires' />
          </MenuItem>
        )}
      </Menu>
    </AppBar>
  );
};

const App: React.FC = () => {
  const { isAuthenticated, loading, unauthorized } = useAuth0();

  const theme = useMemo(
    () =>
      createMuiTheme({
        palette: {
          primary: { main: yellow['900'] },
          secondary: { main: '#B71C1C' },
          type: 'dark',
        },
      }),
    [],
  );

  if (unauthorized) return <NoAccess theme={theme} displayName='Inflow' />;

  return (
    <SnackbarProvider maxSnack={1}>
      <SignalRProvider
        negotiateEndpoint={`${process.env['REACT_APP_API_ENDPOINT']}hub?sessionId=${sessionId}`}
      >
        <SessionsProvider sessionId={sessionId}>
          <ClientsProvider
            endpoint={process.env.REACT_APP_CLIENTS_ENDPOINT!}
            loadOnInit
          >
            <DatasetsProvider>
              <QuestionnairesProvider>
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <UpdateNotify />
                  {(loading || !isAuthenticated) && <Loading />}
                  {!loading && isAuthenticated && (
                    <Router history={history}>
                      <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <Box
                          height='100vh'
                          display='flex'
                          flexDirection='column'
                        >
                          <AppBarWrapper />
                          <Routes />
                        </Box>
                      </MuiPickersUtilsProvider>
                    </Router>
                  )}
                </ThemeProvider>
              </QuestionnairesProvider>
            </DatasetsProvider>
          </ClientsProvider>
        </SessionsProvider>
      </SignalRProvider>
    </SnackbarProvider>
  );
};

export default process.env.NODE_ENV === 'development' ? hot(App) : App;
